import { useState } from "react";
import { TrendingDown, TrendingUp, Star, Trophy } from "lucide-react";
import { fmt } from "../../context/AppContext";

const UK_AVERAGES = [
  { key: "rent", label: "Rent / Mortgage", average: 1100, icon: "🏠", description: "UK avg monthly rent (ONS 2024/25)" },
  { key: "groceries", label: "Groceries", average: 187, icon: "🛒", description: "UK avg household monthly (ONS)" },
  { key: "energy", label: "Energy Bills", average: 117, icon: "⚡", description: "UK avg monthly energy (Ofgem 2024)" },
  { key: "transport", label: "Transport", average: 150, icon: "🚗", description: "Commuting + fuel avg (RAC 2024)" },
  { key: "mobile", label: "Mobile Phone", average: 37, icon: "📱", description: "UK avg monthly mobile bill" },
];

function ScoreStars({ score }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`w-6 h-6 transition-colors ${
            i <= Math.round(score)
              ? "fill-amber-400 text-amber-400"
              : "text-slate-700"
          }`}
        />
      ))}
    </div>
  );
}

function getScoreLabel(score) {
  if (score >= 4.5) return { label: "Money Master", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" };
  if (score >= 3.5) return { label: "Savvy Saver", color: "text-sky-400", bg: "bg-sky-500/10 border-sky-500/20" };
  if (score >= 2.5) return { label: "On Track", color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" };
  if (score >= 1.5) return { label: "Room to Improve", color: "text-orange-400", bg: "bg-orange-500/10 border-orange-500/20" };
  return { label: "Time for a Budget Review", color: "text-rose-400", bg: "bg-rose-500/10 border-rose-500/20" };
}

export default function YouVsAverage() {
  const [values, setValues] = useState({ rent: 1100, groceries: 187, energy: 117, transport: 150, mobile: 37 });

  const set = (key, val) => setValues((v) => ({ ...v, [key]: val }));

  const score = UK_AVERAGES.reduce((total, item) => {
    const diff = item.average - values[item.key];
    const pct = (diff / item.average) * 100;
    if (pct >= 20) return total + 1;
    if (pct >= 5) return total + 0.5;
    if (diff >= 0) return total + 0.25;
    return total;
  }, 0);

  const clampedScore = Math.min(5, Math.max(0, score));
  const scoreLabel = getScoreLabel(clampedScore);

  const totalYours = Object.values(values).reduce((s, v) => s + Number(v), 0);
  const totalAvg = UK_AVERAGES.reduce((s, i) => s + i.average, 0);
  const totalSaving = totalAvg - totalYours;

  return (
    <div className="space-y-6">
      {/* Score Hero */}
      <div data-testid="savvy-score-hero" className="relative bg-slate-900/40 border border-white/5 rounded-xl p-6 overflow-hidden">
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 80% 50%, rgba(251,191,36,0.04), transparent)" }} />
        <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Trophy className="w-5 h-5 text-amber-400" />
              <span className="text-amber-400 text-sm font-medium uppercase tracking-widest">Your Money Savvy Score</span>
            </div>
            <div className="flex items-center gap-4 mb-3">
              <p data-testid="savvy-score" className="font-outfit font-bold text-6xl text-white tabular-nums">
                {clampedScore.toFixed(1)}
                <span className="text-slate-600 text-2xl font-normal">/5</span>
              </p>
            </div>
            <ScoreStars score={clampedScore} />
          </div>
          <div className="flex flex-col gap-3">
            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border text-base font-semibold ${scoreLabel.bg} ${scoreLabel.color}`}>
              <Trophy className="w-4 h-4" /> {scoreLabel.label}
            </span>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-slate-800/40 rounded-lg p-3 text-center">
                <p className="text-slate-500 text-xs">Your Total</p>
                <p className="font-outfit font-bold text-white tabular-nums">{fmt(totalYours)}/mo</p>
              </div>
              <div className="bg-slate-800/40 rounded-lg p-3 text-center">
                <p className="text-slate-500 text-xs">{totalSaving >= 0 ? "Saving vs Avg" : "Above Avg"}</p>
                <p className={`font-outfit font-bold tabular-nums ${totalSaving >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                  {totalSaving >= 0 ? "+" : ""}{fmt(totalSaving)}/mo
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Table */}
      <div data-testid="comparison-table" className="bg-slate-900/40 border border-white/5 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-white/5">
          <h2 className="font-outfit font-semibold text-white">Bill-by-Bill Comparison</h2>
          <p className="text-slate-500 text-xs mt-0.5">How do your costs compare to UK averages?</p>
        </div>
        <div className="divide-y divide-white/5">
          {UK_AVERAGES.map((item) => {
            const yours = Number(values[item.key]) || 0;
            const diff = item.average - yours;
            const pct = item.average > 0 ? (diff / item.average) * 100 : 0;
            const saving = diff >= 0;
            return (
              <div key={item.key} className="grid grid-cols-1 md:grid-cols-2 gap-4 px-5 py-4 hover:bg-white/[0.02] transition-colors">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{item.icon}</span>
                  <div>
                    <p className="text-slate-200 text-sm font-medium">{item.label}</p>
                    <p className="text-slate-600 text-xs">{item.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 md:justify-end">
                  {/* Your value input */}
                  <div className="flex-1 md:flex-none">
                    <label className="text-slate-600 text-[10px] block mb-1">Your Cost</label>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-xs">£</span>
                      <input
                        type="number"
                        data-testid={`input-${item.key}`}
                        value={values[item.key]}
                        onChange={(e) => set(item.key, Number(e.target.value) || 0)}
                        className="w-24 bg-slate-800/60 border border-slate-700/50 focus:border-slate-600 rounded-lg pl-5 pr-2 py-1.5 text-white text-sm font-semibold outline-none transition-colors text-right"
                      />
                    </div>
                  </div>
                  {/* UK Average */}
                  <div className="text-center">
                    <p className="text-slate-600 text-[10px] mb-1">UK Average</p>
                    <p className="text-slate-400 font-semibold text-sm tabular-nums">{fmt(item.average)}</p>
                  </div>
                  {/* Difference */}
                  <div className="text-right min-w-[80px]">
                    <p className="text-slate-600 text-[10px] mb-1">{saving ? "Saving" : "Over by"}</p>
                    <div className={`flex items-center justify-end gap-1 ${saving ? "text-emerald-400" : "text-rose-400"}`}>
                      {saving ? <TrendingDown className="w-3.5 h-3.5" /> : <TrendingUp className="w-3.5 h-3.5" />}
                      <span className="font-outfit font-bold text-sm tabular-nums">{fmt(Math.abs(diff))}</span>
                    </div>
                    <p className={`text-[10px] ${saving ? "text-emerald-600" : "text-rose-600"}`}>
                      {Math.abs(pct).toFixed(0)}% {saving ? "below" : "above"} avg
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="px-5 py-3 bg-slate-800/20 border-t border-white/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <span className="text-slate-500 text-sm">Monthly total</span>
          <div className="flex items-center gap-6">
            <span className="text-slate-400 text-sm tabular-nums">Yours: <span className="text-white font-bold">{fmt(totalYours)}</span></span>
            <span className="text-slate-400 text-sm tabular-nums">UK Avg: <span className="text-slate-300 font-bold">{fmt(totalAvg)}</span></span>
            <span className={`text-sm tabular-nums font-bold ${totalSaving >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
              {totalSaving >= 0 ? "Saving " : "Over "}{fmt(Math.abs(totalSaving))}
            </span>
          </div>
        </div>
      </div>

      {/* Tips */}
      <div className="bg-slate-900/30 border border-white/5 rounded-xl p-5">
        <h3 className="font-outfit font-semibold text-white text-sm mb-3">How the Score is Calculated</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-slate-500">
          <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-lg p-3">
            <p className="text-emerald-400 font-semibold mb-1">+1.0 point</p>
            <p>More than 20% below the UK average per category</p>
          </div>
          <div className="bg-sky-500/5 border border-sky-500/10 rounded-lg p-3">
            <p className="text-sky-400 font-semibold mb-1">+0.5 points</p>
            <p>5–20% below the UK average per category</p>
          </div>
          <div className="bg-slate-800/40 border border-white/5 rounded-lg p-3">
            <p className="text-slate-300 font-semibold mb-1">+0.25 points</p>
            <p>At or slightly below the UK average per category</p>
          </div>
        </div>
      </div>
    </div>
  );
}
